import { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import api from "../utils/api";
import { useAuth } from "../context/AuthContext";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import DogEditModal from "../components/DogEditModal";
import {
  buildPatchPayload,
  compactDogFilter,
  dogToFilterForm,
} from "../utils/dogEditForm";

const Container = styled.div`
  display: flex;
  height: 100vh;
  width: 100%;
  background: linear-gradient(135deg, #fff7ed, #fff9f2);
  overflow: hidden;
  box-sizing: border-box;
`;

const MapSection = styled.div`
  flex: 1;
  min-width: 0;
  padding: 12px;
  border-right: 2px solid #ffedd5;
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
  background: linear-gradient(135deg, #fff7ed, #fff9f2);
  box-sizing: border-box;
`;

const Half = styled.div`
  flex: 1;
  min-width: 0;
  padding: 12px;
  border-right: ${(props) =>
    props.borderRight ? "2px solid #ffedd5" : "none"};
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
  box-sizing: border-box;
`;

const Title = styled.h2`
  margin-bottom: 12px;
  color: #1f2937;
  font-size: 18px;
  font-weight: 700;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  flex-shrink: 0;
`;

const EditButton = styled.button`
  background: #c2410c;
  color: #fff;
  padding: 6px 14px;
  border-radius: 8px;
  border: none;
  font-weight: 600;
  cursor: pointer;
  font-size: 13px;
  flex-shrink: 0;
  transition: background 0.2s;
  &:hover {
    background: #9a3412;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const ImageCarouselContainer = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const CarouselWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 400px;
  aspect-ratio: 1;
  border-radius: 8px;
  overflow: hidden;
  background: #f9fafb;
  border: 2px solid #ffedd5;
`;

const CarouselImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: ${(props) => (props.active ? "block" : "none")};
`;

const CarouselButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.6);
  color: white;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: bold;
  z-index: 10;
  transition: background 0.2s;
  &:hover {
    background: rgba(0, 0, 0, 0.8);
  }
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
  ${(props) => (props.direction === "prev" ? "left: 10px;" : "right: 10px;")}
`;

const CarouselDots = styled.div`
  display: flex;
  justify-content: center;
  gap: 6px;
  margin-top: 12px;
`;

const Dot = styled.button`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: none;
  background: ${(props) => (props.active ? "#c2410c" : "#d1d5db")};
  cursor: pointer;
  padding: 0;
  transition: background 0.2s;
  &:hover {
    background: ${(props) => (props.active ? "#c2410c" : "#9ca3af")};
  }
`;

const NoImagePlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 14px;
  background: #f3f4f6;
`;

const DetailsSection = styled.div`
  background: #fffdf9;
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #f5f5f5;
  font-size: 14px;
  gap: 8px;
  min-width: 0;

  &:last-child {
    border-bottom: none;
  }
`;

const Label = styled.span`
  font-weight: 600;
  color: #374151;
  flex-shrink: 0;
`;

const Value = styled.span`
  color: #6b7280;
  text-align: right;
  flex: 1;
  min-width: 0;
  word-break: break-word;
  overflow-wrap: break-word;
`;

const CoordValue = styled(Value)`
  color: ${(props) => props.$color};
  font-weight: 500;
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 16px;
  gap: 12px;
`;

const NavButton = styled.button`
  background: linear-gradient(90deg, #9ca3af, #6b7280);
  color: #fff;
  padding: 8px 16px;
  border-radius: 8px;
  border: none;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ActionButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const MergeButton = styled.button`
  background: linear-gradient(90deg, #3b82f6, #60a5fa);
  color: #fff;
  padding: 8px 16px;
  border-radius: 8px;
  border: none;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }
`;

const UniqueButton = styled.button`
  background: linear-gradient(90deg, #10b981, #34d399);
  color: #fff;
  padding: 8px 16px;
  border-radius: 8px;
  border: none;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }
`;

const LoadingText = styled.div`
  text-align: center;
  padding: 40px;
  color: #6b7280;
  font-size: 16px;
`;

const ErrorText = styled.div`
  text-align: center;
  padding: 40px;
  color: #dc2626;
  font-size: 16px;
`;

const EmptyText = styled.div`
  text-align: center;
  padding: 40px;
  color: #9ca3af;
  font-size: 16px;
`;

const MapWrapper = styled.div`
  height: 100%;
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid #ffedd5;
  background: #f9fafb;
  flex: 1;
`;

const hasLatLng = (point) =>
  point?.latitude != null && point?.longitude != null;

const createPinIcon = (color, size) => {
  const svgIcon = `
    <svg width="${size}" height="${size * 1.5}" viewBox="0 0 25 41" xmlns="http://www.w3.org/2000/svg">
      <path fill="${color}" stroke="#fff" stroke-width="1.5" d="M12.5 0C5.6 0 0 5.6 0 12.5c0 8.7 12.5 28.5 12.5 28.5S25 21.2 25 12.5C25 5.6 19.4 0 12.5 0z"/>
      <circle fill="#fff" cx="12.5" cy="12.5" r="5"/>
    </svg>
  `;
  return L.divIcon({
    html: svgIcon,
    className: "custom-marker-icon",
    iconSize: [size, size * 1.5],
    iconAnchor: [size / 2, size * 1.5],
    popupAnchor: [0, -size * 1.5],
  });
};

const observedPinIcon = createPinIcon("#ef4444", 35);
const finalPinIcon = createPinIcon("#3b82f6", 25);
const currentFinalPinIcon = createPinIcon("#16a34a", 32);

const MapFitBounds = ({ pointsKey }) => {
  const map = useMap();

  useEffect(() => {
    if (!pointsKey) return;
    const points = pointsKey.split("|").map((pair) => {
      const [lat, lng] = pair.split(",").map(Number);
      return [lat, lng];
    });
    if (points.length === 1) {
      map.setView(points[0], 15);
      return;
    }
    map.fitBounds(points, { padding: [20, 20] });
  }, [map, pointsKey]);

  return null;
};

const ObservationMap = ({ observedCoords, finalDogs, currentFinalId }) => {
  const finalPoints = (finalDogs || [])
    .filter((dog) => hasLatLng(dog.homeCentroid))
    .map((dog) => ({
      id: dog.id,
      latitude: dog.homeCentroid.latitude,
      longitude: dog.homeCentroid.longitude,
    }));

  const points = [];
  if (hasLatLng(observedCoords)) {
    points.push([observedCoords.latitude, observedCoords.longitude]);
  }
  finalPoints.forEach((point) => {
    points.push([point.latitude, point.longitude]);
  });

  if (points.length === 0) return null;

  const pointsKey = [...new Set(points.map((point) => point.join(",")))].join(
    "|",
  );

  return (
    <MapWrapper>
      <MapContainer
        center={points[0]}
        zoom={13}
        style={{ height: "100%", width: "100%" }}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution="&copy; OpenStreetMap contributors"
        />
        <MapFitBounds pointsKey={pointsKey} />
        {hasLatLng(observedCoords) && (
          <Marker
            position={[observedCoords.latitude, observedCoords.longitude]}
            icon={observedPinIcon}
          />
        )}
        {finalPoints.map((point) => (
          <Marker
            key={point.id}
            position={[point.latitude, point.longitude]}
            icon={
              point.id === currentFinalId ? currentFinalPinIcon : finalPinIcon
            }
          />
        ))}
      </MapContainer>
    </MapWrapper>
  );
};

// Helper function to format enum values for display
const formatEnum = (value) => {
  if (!value) return "N/A";
  return value
    .split("_")
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(" ");
};

// Helper function to format array values
const formatArray = (arr) => {
  if (!arr || arr.length === 0) return "None";
  return arr.map(formatEnum).join(", ");
};

// Transform API dog observation to component format
const transformObservedDog = (dog) => {
  if (!dog) return null;

  const identification = dog.fields?.identification;

  return {
    id: dog._id,
    photos: dog.uploads || [],
    gender: formatEnum(dog.fields?.demographics?.gender),
    age: formatEnum(dog.fields?.demographics?.ageGroup),
    breed:
      dog.fields?.demographics?.breedOther ||
      formatEnum(dog.fields?.demographics?.breed),
    primaryColor:
      dog.fields?.appearance?.primaryColorOther ||
      formatEnum(dog.fields?.appearance?.primaryColor),
    secondaryColor:
      dog.fields?.appearance?.secondaryColorOther ||
      formatEnum(dog.fields?.appearance?.secondaryColor) ||
      "N/A",
    size: formatEnum(dog.fields?.body?.size),
    condition: formatEnum(dog.fields?.body?.bodyCondition),
    reproductiveStatus: formatEnum(dog.fields?.reproductiveStatus),
    identification: identification || "-",
    collar: identification === "COLLAR" ? "Yes" : "No",
    scars: formatArray(dog.fields?.scars),
    spots: formatArray(dog.fields?.spots),
    injuries: formatArray(dog.fields?.injuries),
    coords: dog.coords || null,
    remarks: dog.fields?.remarks || "-",
  };
};

// Transform API dog to component format
const transformAvailableDog = (dog) => {
  if (!dog) return null;

  const identification = dog.fields?.identification;

  return {
    id: dog._id,
    photos: dog.uploads || [],
    gender: formatEnum(dog.fields?.demographics?.gender),
    age: formatEnum(dog.fields?.demographics?.ageGroup),
    breed:
      dog.fields?.demographics?.breedOther ||
      formatEnum(dog.fields?.demographics?.breed),
    primaryColor:
      dog.fields?.appearance?.primaryColorOther ||
      formatEnum(dog.fields?.appearance?.primaryColor),
    secondaryColor:
      dog.fields?.appearance?.secondaryColorOther ||
      formatEnum(dog.fields?.appearance?.secondaryColor) ||
      "N/A",
    size: formatEnum(dog.fields?.body?.size),
    condition: formatEnum(dog.fields?.body?.bodyCondition),
    reproductiveStatus: formatEnum(
      dog.reproductiveStatus || dog.fields?.reproductiveStatus,
    ),
    identification: identification || "-",
    collar: identification === "COLLAR" ? "Yes" : "No",
    scars: formatArray(dog.fields?.scars),
    spots: formatArray(dog.fields?.spots),
    injuries: formatArray(dog.fields?.injuries),
    homeCentroid: dog.homeCentroid || null,
    remarks: dog.fields?.remarks || "-",
  };
};

export default function DogObservation() {
  const { id: surveyId } = useParams();
  const [searchParams] = useSearchParams();
  const routeId = searchParams.get("routeId");
  const { token } = useAuth();

  const [currentDogIndex, setCurrentDogIndex] = useState(0);
  const [currentObservedDogIndex, setCurrentObservedDogIndex] = useState(0);
  const [observedDogs, setObservedDogs] = useState([]);
  const [rawObservations, setRawObservations] = useState([]);
  const [observedDog, setObservedDog] = useState(null);
  const [availableDogs, setAvailableDogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingAvailableDogs, setLoadingAvailableDogs] = useState(false);
  const [error, setError] = useState(null);
  const [merging, setMerging] = useState(false);
  const [markingUnique, setMarkingUnique] = useState(false);
  const [editModalObservation, setEditModalObservation] = useState(null);
  const [savingObservation, setSavingObservation] = useState(false);
  const [patchError, setPatchError] = useState(null);
  const [dogFilter, setDogFilter] = useState(null);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const dogsRequestRef = useRef(0);

  // Fetch observed dogs
  useEffect(() => {
    if (surveyId) {
      fetchObservedDogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyId]);

  // Update observed dog when index changes
  useEffect(() => {
    if (
      observedDogs.length > 0 &&
      currentObservedDogIndex < observedDogs.length
    ) {
      setObservedDog(observedDogs[currentObservedDogIndex]);
    }
  }, [currentObservedDogIndex, observedDogs]);

  const currentRawObservation =
    rawObservations[currentObservedDogIndex] || null;
  const currentRawObservationId = currentRawObservation?._id || null;

  // Prefill gender, breed, and primary color whenever the observed dog changes.
  useEffect(() => {
    if (!currentRawObservationId) {
      setDogFilter(null);
      return;
    }
    setDogFilter(dogToFilterForm(currentRawObservation));
    setFilterModalOpen(false);
    // Only the observed dog identity should reset the prefill.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRawObservationId]);

  const observedRouteId = (() => {
    const fromObs = rawObservations[0]?.routeId;
    if (!fromObs) return null;
    return typeof fromObs === "object" ? fromObs._id : fromObs;
  })();

  // Final-data dogs follow the selected filter, not similarity.
  useEffect(() => {
    if (!dogFilter) {
      setLoadingAvailableDogs(false);
      return undefined;
    }

    const routeToUse = routeId || observedRouteId;
    if (!routeToUse) {
      setLoadingAvailableDogs(false);
      return undefined;
    }

    const requestId = ++dogsRequestRef.current;
    let cancelled = false;

    const load = async () => {
      try {
        setLoadingAvailableDogs(true);
        setError(null);
        setAvailableDogs([]);
        const res = await api.get("/api/dogs", {
          params: {
            routeId: routeToUse,
            excludeLinkedSurveyId: surveyId,
            filter: JSON.stringify(compactDogFilter(dogFilter)),
          },
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled || requestId !== dogsRequestRef.current) return;
        const dogs = res.data?.data || [];
        const transformedDogs = dogs.map(transformAvailableDog).filter(Boolean);
        setAvailableDogs(transformedDogs);
        setCurrentDogIndex(0);
      } catch (err) {
        if (cancelled || requestId !== dogsRequestRef.current) return;
        console.error("Error fetching available dogs:", err);
        setError("Failed to load available dogs");
        setAvailableDogs([]);
      } finally {
        if (!cancelled && requestId === dogsRequestRef.current) {
          setLoadingAvailableDogs(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [dogFilter, routeId, observedRouteId, surveyId, token]);

  const fetchObservedDogs = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get("/api/dog-observations", {
        params: { surveyId },
        headers: { Authorization: `Bearer ${token}` },
      });

      const observations = res.data?.data || [];
      if (observations.length > 0) {
        setRawObservations(observations);
        const transformedObservations = observations
          .map(transformObservedDog)
          .filter(Boolean);
        setObservedDogs(transformedObservations);
        setCurrentObservedDogIndex(0);
        setObservedDog(transformedObservations[0]);
      } else {
        setRawObservations([]);
        setObservedDogs([]);
        setObservedDog(null);
      }
    } catch (err) {
      console.error("Error fetching observed dogs:", err);
      setError("Failed to load observed dogs");
    } finally {
      setLoading(false);
    }
  };

  const currentDog = availableDogs[currentDogIndex];

  const handlePrev = () => {
    setCurrentDogIndex((prev) =>
      prev > 0 ? prev - 1 : availableDogs.length - 1,
    );
  };

  const handleNext = () => {
    setCurrentDogIndex((prev) =>
      prev < availableDogs.length - 1 ? prev + 1 : 0,
    );
  };

  const handleObservedDogPrev = () => {
    setCurrentObservedDogIndex((prev) =>
      prev > 0 ? prev - 1 : observedDogs.length - 1,
    );
  };

  const handleObservedDogNext = () => {
    setCurrentObservedDogIndex((prev) =>
      prev < observedDogs.length - 1 ? prev + 1 : 0,
    );
  };

  const handleMerge = async () => {
    if (!currentDog || !observedDog) {
      alert("Please select a dog to merge with");
      return;
    }

    try {
      setMerging(true);
      await api.post(
        `/api/dogs/${currentDog.id}/observations`,
        { observationId: observedDog.id },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      alert("Dog merged successfully!");
      // Optionally refresh data or navigate away
      window.location.reload();
    } catch (err) {
      console.error("Error merging observation:", err);
      alert(err.response?.data?.message || "Failed to merge observation");
    } finally {
      setMerging(false);
    }
  };

  const openFilterModal = () => {
    if (!dogFilter) return;
    setEditModalObservation(null);
    setPatchError(null);
    setFilterModalOpen(true);
  };

  const closeFilterModal = () => {
    setFilterModalOpen(false);
  };

  const applyDogFilter = (form) => {
    setDogFilter(JSON.parse(JSON.stringify(form)));
    setFilterModalOpen(false);
  };

  const openEditModal = () => {
    const raw = rawObservations[currentObservedDogIndex];
    if (!raw) return;
    setFilterModalOpen(false);
    setPatchError(null);
    setEditModalObservation(raw);
  };

  const closeEditModal = () => {
    if (savingObservation) return;
    setEditModalObservation(null);
    setPatchError(null);
  };

  const saveObservationPatch = async (form) => {
    if (!editModalObservation) return;
    setSavingObservation(true);
    setPatchError(null);
    try {
      const payload = buildPatchPayload(form);
      const res = await api.patch(
        `/api/dog-observations/${editModalObservation._id}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const updated = res.data?.data;
      if (updated) {
        setRawObservations((prev) =>
          prev.map((obs) => (obs._id === updated._id ? updated : obs)),
        );
        const transformed = transformObservedDog(updated);
        setObservedDogs((prev) =>
          prev.map((dog) => (dog.id === updated._id ? transformed : dog)),
        );
        if (observedDog?.id === updated._id) {
          setObservedDog(transformed);
        }
      }
      setEditModalObservation(null);
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Failed to save observation";
      setPatchError(msg);
    } finally {
      setSavingObservation(false);
    }
  };

  const handleMarkAsUnique = async () => {
    if (!observedDog) {
      alert("No observation to mark as unique");
      return;
    }

    try {
      setMarkingUnique(true);
      await api.post(
        `/api/dog-observations/${observedDog.id}/markAsUnique`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      alert("Dog marked as unique successfully!");
      // Optionally refresh data or navigate away
      window.location.reload();
    } catch (err) {
      console.error("Error marking as unique:", err);
      alert(err.response?.data?.message || "Failed to mark dog as unique");
    } finally {
      setMarkingUnique(false);
    }
  };

  const ImageCarousel = ({ images }) => {
    const [currentIndex, setCurrentIndex] = useState(0);

    if (!images || images.length === 0) {
      return (
        <ImageCarouselContainer>
          <CarouselWrapper>
            <NoImagePlaceholder>No images available</NoImagePlaceholder>
          </CarouselWrapper>
        </ImageCarouselContainer>
      );
    }

    const goToPrevious = () => {
      setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    };

    const goToNext = () => {
      setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    };

    return (
      <ImageCarouselContainer>
        <CarouselWrapper>
          {images.map((url, index) => (
            <CarouselImage
              key={index}
              src={url}
              alt={`Dog photo ${index + 1}`}
              active={index === currentIndex}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          ))}
          {images.length > 1 && (
            <>
              <CarouselButton direction="prev" onClick={goToPrevious}>
                ‹
              </CarouselButton>
              <CarouselButton direction="next" onClick={goToNext}>
                ›
              </CarouselButton>
            </>
          )}
        </CarouselWrapper>
      </ImageCarouselContainer>
    );
  };

  const DogDetails = ({
    dog,
    title,
    hideTitle = false,
    observationCoords,
    homeCentroid,
  }) => {
    if (!dog) {
      return (
        <>
          {!hideTitle && <Title>{title}</Title>}
          <ScrollArea>
            <EmptyText>No dog data available</EmptyText>
          </ScrollArea>
        </>
      );
    }

    const formatCoords = (lat, lng) =>
      lat != null && lng != null
        ? `${Number(lat).toFixed(6)}, ${Number(lng).toFixed(6)}`
        : "N/A";

    return (
      <>
        {!hideTitle && <Title>{title}</Title>}
        <ScrollArea>
          <ImageCarousel images={dog.photos || []} />

          <DetailsSection>
            {observationCoords?.latitude != null &&
              observationCoords?.longitude != null && (
                <DetailRow>
                  <Label>Observed location:</Label>
                  <CoordValue $color="#dc2626">
                    {formatCoords(
                      observationCoords.latitude,
                      observationCoords.longitude,
                    )}
                  </CoordValue>
                </DetailRow>
              )}
            {homeCentroid?.latitude != null &&
              homeCentroid?.longitude != null && (
                <DetailRow>
                  <Label>Home location:</Label>
                  <CoordValue $color="#16a34a">
                    {formatCoords(
                      homeCentroid.latitude,
                      homeCentroid.longitude,
                    )}
                  </CoordValue>
                </DetailRow>
              )}
            <DetailRow>
              <Label>Gender:</Label>
              <Value>{dog.gender}</Value>
            </DetailRow>
            <DetailRow>
              <Label>Age:</Label>
              <Value>{dog.age}</Value>
            </DetailRow>
            <DetailRow>
              <Label>Breed:</Label>
              <Value>{dog.breed}</Value>
            </DetailRow>
            <DetailRow>
              <Label>Primary Color:</Label>
              <Value>{dog.primaryColor}</Value>
            </DetailRow>
            <DetailRow>
              <Label>Secondary Color:</Label>
              <Value>{dog.secondaryColor}</Value>
            </DetailRow>
            <DetailRow>
              <Label>Size:</Label>
              <Value>{dog.size}</Value>
            </DetailRow>
            <DetailRow>
              <Label>Condition:</Label>
              <Value>{dog.condition}</Value>
            </DetailRow>
            <DetailRow>
              <Label>Reproductive Status:</Label>
              <Value>{dog.reproductiveStatus}</Value>
            </DetailRow>
            <DetailRow>
              <Label>Identification:</Label>
              <Value>{dog.identification}</Value>
            </DetailRow>
            <DetailRow>
              <Label>Collar:</Label>
              <Value>{dog.collar}</Value>
            </DetailRow>
            <DetailRow>
              <Label>Scars:</Label>
              <Value>{dog.scars}</Value>
            </DetailRow>
            <DetailRow>
              <Label>Spots:</Label>
              <Value>{dog.spots}</Value>
            </DetailRow>
            <DetailRow>
              <Label>Injuries:</Label>
              <Value>{dog.injuries}</Value>
            </DetailRow>
            <DetailRow>
              <Label>Remarks:</Label>
              <Value>{dog.remarks}</Value>
            </DetailRow>
          </DetailsSection>
        </ScrollArea>
      </>
    );
  };

  if (loading) {
    return (
      <Container>
        <MapSection>
          <LoadingText>Loading map...</LoadingText>
        </MapSection>
        <Half borderRight>
          <LoadingText>Loading observed dog...</LoadingText>
        </Half>
        <Half>
          <LoadingText>Loading available dogs...</LoadingText>
        </Half>
      </Container>
    );
  }

  if (error && !observedDog) {
    return (
      <Container>
        <MapSection>
          <EmptyText>No map data</EmptyText>
        </MapSection>
        <Half borderRight>
          <ErrorText>{error}</ErrorText>
        </Half>
        <Half>
          <EmptyText>No data available</EmptyText>
        </Half>
      </Container>
    );
  }

  return (
    <Container>
      <MapSection>
        <Title>Map</Title>
        <ObservationMap
          observedCoords={observedDog?.coords}
          finalDogs={availableDogs}
          currentFinalId={currentDog?.id}
        />
      </MapSection>
      <Half borderRight>
        <SectionHeader>
          <Title style={{ marginBottom: 0 }}>
            {`NEW DATA (${
              observedDogs.length > 0 ? currentObservedDogIndex + 1 : 0
            }/${observedDogs.length})`}
          </Title>
          {observedDog && (
            <EditButton type="button" onClick={openEditModal}>
              Edit
            </EditButton>
          )}
        </SectionHeader>
        <DogDetails
          dog={observedDog}
          hideTitle
          observationCoords={observedDog?.coords}
        />

        {observedDogs.length > 1 && (
          <ButtonContainer>
            <NavButton
              onClick={handleObservedDogPrev}
              disabled={loadingAvailableDogs || observedDogs.length === 0}
            >
              Prev
            </NavButton>
            <div style={{ flex: 1 }} />
            <NavButton
              onClick={handleObservedDogNext}
              disabled={loadingAvailableDogs || observedDogs.length === 0}
            >
              Next
            </NavButton>
          </ButtonContainer>
        )}
      </Half>

      <Half>
        <SectionHeader>
          <Title style={{ marginBottom: 0 }}>
            {`FINAL DATA (${
              availableDogs.length > 0 ? currentDogIndex + 1 : 0
            }/${availableDogs.length})`}
          </Title>
          <EditButton
            type="button"
            onClick={openFilterModal}
            disabled={!dogFilter}
          >
            Filter
          </EditButton>
        </SectionHeader>
        {loadingAvailableDogs ? (
          <ScrollArea>
            <LoadingText>Loading available dogs...</LoadingText>
          </ScrollArea>
        ) : (
          <DogDetails
            dog={currentDog}
            hideTitle
            homeCentroid={currentDog?.homeCentroid}
          />
        )}

        {!loadingAvailableDogs && availableDogs.length > 0 ? (
          <ButtonContainer>
            <NavButton
              onClick={handlePrev}
              disabled={availableDogs.length === 0}
            >
              Prev
            </NavButton>

            {observedDog && (
              <ActionButtonGroup>
                <MergeButton
                  onClick={handleMerge}
                  disabled={merging || !currentDog}
                >
                  {merging ? "Merging..." : "Merge"}
                </MergeButton>
                <UniqueButton
                  onClick={handleMarkAsUnique}
                  disabled={markingUnique}
                >
                  {markingUnique ? "Marking..." : "Mark as Unique"}
                </UniqueButton>
              </ActionButtonGroup>
            )}

            <NavButton
              onClick={handleNext}
              disabled={availableDogs.length === 0}
            >
              Next
            </NavButton>
          </ButtonContainer>
        ) : (
          !loadingAvailableDogs && observedDog && (
            <ButtonContainer>
              <ActionButtonGroup style={{ margin: "0 auto" }}>
                <UniqueButton
                  onClick={handleMarkAsUnique}
                  disabled={markingUnique}
                >
                  {markingUnique ? "Marking..." : "Mark as Unique"}
                </UniqueButton>
              </ActionButtonGroup>
            </ButtonContainer>
          )
        )}
      </Half>

      {editModalObservation && (
        <DogEditModal
          dog={editModalObservation}
          title={`Edit observation — ${editModalObservation._id}`}
          saving={savingObservation}
          error={patchError}
          onClose={closeEditModal}
          onSave={saveObservationPatch}
        />
      )}

      {filterModalOpen && dogFilter && (
        <DogEditModal
          initialForm={dogFilter}
          title="Filter dogs"
          submitLabel="Apply filter"
          onClose={closeFilterModal}
          onSave={applyDogFilter}
        />
      )}
    </Container>
  );
}
